import { useState, VFC } from 'react'
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form'
import TextareaAutosize from 'react-textarea-autosize'
import { joinEvent } from 'src/api/event'
import { useMasterData } from 'src/api/masterData'
import { ExternalLinkIcon } from 'src/assets/svgs'
import { Event } from 'src/models/Event'
import { LoginUser } from 'src/models/User'
import { _contributiontext, _formalert } from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import styled from 'styled-components'
import { ItemTitle } from '../Contributions/ContributionForm/ItemTitle'
import { SalesDemoModal } from '../Messages/Modals/SalesDemoModal'
import { formInputStyle } from '../Profile/formComponents'
import {
  EventDisableButton,
  EventSubmitButton,
  GoogleFormLink,
  ModalCloseButton,
} from './styles'

type ValuesType = {
  id: number | null
  eventId: number | null
  companyId: number | undefined
  companyName: string | undefined
  adminName: string
  position: string
  email: string
  phoneNumber: string
  note: string | null
}

type EventFormBaseProps = {
  user: LoginUser
  event: Event
  modalClose?: VoidFunction
}

const NOTE_MAX_LENGTH = 800 // 備考(note) の最大文字数

export const EventFormBase: VFC<EventFormBaseProps> = ({
  user,
  event,
  modalClose,
}) => {
  const [submitSucceeded, setSubmitSucceeded] = useState(false)
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [showSalesDemoModal, setShowSalesDemoModal] = useState<boolean>(false)
  const initialValues = {
    adminName: user.profile.name,
    email: user.profile.email,
    position: user.profile.position?.value,
    phoneNumber: user.profile.phoneNumber,
    note: '',
  }

  const methods = useForm<any>({
    defaultValues: initialValues,
  })

  const { handleSubmit, formState, watch } = methods

  const { data } = useMasterData()
  const options = data?.positions || []

  const handleOnSubmit: SubmitHandler<ValuesType> = async (values) => {
    if (user.plan.salesDemo) {
      setShowSalesDemoModal(true)
      return
    }
    const res = await joinEvent({
      id: null,
      eventId: event.id,
      companyId: user.id,
      companyName: user.profile.name,
      adminName: values.adminName,
      positionText: values.position,
      email: values.email,
      phoneNumber: values.phoneNumber,
      note: values.note,
    })
    if (res.error?.errorMessages) {
      setErrorMessages(res.error.errorMessages)
      return
    }
    setErrorMessages([])
    setSubmitSucceeded(true)
  }

  // GoogleフォームURLが設定されている場合は外部リンクを表示
  if (event.registrationType === 'google_form' && event.googleFormUrl)
    return (
      <StyledForm>
        <h2>参加申し込み</h2>
        <Description>
          下記のボタンよりGoogleFormにてお申し込みください
          <br />
          なお、こちらはお申し込み受付のみとなり、お席のご用意を確定するものではございませんので予めご了承下さい。
        </Description>
        <GoogleFormLink
          href={event.googleFormUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          参加申請する
          <ExternalLinkIcon />
        </GoogleFormLink>
        <GoogleFormNote>Google Formを新しいウィンドウで開きます</GoogleFormNote>
        {!!modalClose && (
          <ModalCloseButtonWrapper>
            <ModalCloseButton type="button" onClick={modalClose}>
              閉じる
            </ModalCloseButton>
          </ModalCloseButtonWrapper>
        )}
      </StyledForm>
    )

  return (
    <FormProvider {...methods}>
      <StyledForm onSubmit={handleSubmit(handleOnSubmit)}>
        <h2>参加申し込みフォーム</h2>
        <Description>
          必須事項をご記入の上、参加申請ボタンを押してください。
          <br />
          なお、こちらはお申し込み受付のみとなり、お席のご用意を確定するものではございませんので予めご了承下さい。
        </Description>

        <InputItem
          id="adminName"
          placeholder="オンリー太郎"
          title="参加者名"
          checked={!!watch('adminName')}
        />

        <InputItem
          id="email"
          placeholder="info@onlsytory.jp"
          title="メールアドレス"
          checked={!!watch('email')}
        />

        <SelectItem
          id="position"
          title="ご役職"
          checked={!!watch('position')}
          options={options}
          placeholder=""
        />

        <InputItem
          id="phoneNumber"
          placeholder="09012345678"
          title="電話番号"
          checked={!!watch('phoneNumber')}
        />

        <NoteItem
          id="note"
          placeholder="お問い合わせ内容をご記載ください"
          minRows={3}
          checked={!!watch('note')}
          title="備考"
        />
        <Alert>
          {!!formState.errors.adminName && (
            <p>{formState.errors.adminName.message}</p>
          )}
          {!!formState.errors.email && <p>{formState.errors.email.message}</p>}
          {!!formState.errors.position && (
            <p>{formState.errors.position.message}</p>
          )}
          {!!formState.errors.phoneNumber && (
            <p>{formState.errors.phoneNumber.message}</p>
          )}
          {!!formState.errors.note && <p>{formState.errors.note.message}</p>}

          {errorMessages.length > 0 && (
            <p>
              {errorMessages.map((message) => (
                <p key={message}>{message}</p>
              ))}
            </p>
          )}
        </Alert>
        <Submitted>
          {submitSucceeded && <p>参加申請が完了しました！</p>}
        </Submitted>
        {event.entried || submitSucceeded ? (
          <EventDisableButton disabled>参加申請済みです</EventDisableButton>
        ) : (
          <EventSubmitButton type="submit">参加申請する</EventSubmitButton>
        )}
        {!!modalClose && (
          <ModalCloseButtonWrapper>
            <ModalCloseButton type="button" onClick={modalClose}>
              閉じる
            </ModalCloseButton>
          </ModalCloseButtonWrapper>
        )}
      </StyledForm>
      {showSalesDemoModal && (
        <SalesDemoModal close={() => setShowSalesDemoModal(false)} />
      )}
    </FormProvider>
  )
}

const StyledForm = styled.form`
  position: relative;
  h2 {
    font-size: 18px;
    font-weight: ${fontWeightBold};
    text-align: center;
    margin-bottom: 24px;
  }
`

const Description = styled.p`
  font-weight: ${fontWeightBold};
  font-size: 12px;
  line-height: 21px;
  text-align: center;
  margin-bottom: 32px;
`
const Alert = styled.div`
  color: ${_formalert};
  margin-bottom: 15px;
  p {
    text-align: center;
  }
`
const Submitted = styled.div`
  color: ${_contributiontext};
  margin-bottom: 15px;
  p {
    text-align: center;
  }
`
const ModalCloseButtonWrapper = styled.div`
  margin-top: 15px;
`

const Input = styled.input`
  ${formInputStyle};
  margin: 8px 0 16px;
  width: 100%;
`

const TextArea = styled(TextareaAutosize)`
  ${formInputStyle};
  line-height: 1.5;
  margin: 8px 0 16px;
  width: 100%;
`

const Select = styled.select`
  ${formInputStyle};
  margin: 8px 0 16px;
  -webkit-appearance: none;
`

const GoogleFormNote = styled.p`
  font-size: 12px;
  text-align: center;
  color: #808080;
  margin-top: 16px;
`

export const InputItem: VFC<{
  title: string
  id: string
  placeholder: string
  checked: boolean
}> = ({ id, title, placeholder, checked }) => {
  const { register } = useFormContext()

  return (
    <>
      <ItemTitle title={title} checked={checked} />
      <Input
        id={id}
        placeholder={placeholder}
        {...register(id, {
          required: `${title}の入力は必須です`,
        })}
      />
    </>
  )
}

export const SelectItem: VFC<{
  title: string
  id: string
  placeholder: string
  checked: boolean
  options: Array<{ label: string; value: string }>
}> = ({ id, title, placeholder, checked, options }) => {
  const { register } = useFormContext()

  return (
    <>
      <ItemTitle title={title} checked={checked} />
      <Select
        id={id}
        placeholder={placeholder}
        {...register(id, {
          required: `${title}の入力は必須です`,
        })}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} label={opt.label}>
            {opt.label}
          </option>
        ))}
      </Select>
    </>
  )
}

export const NoteItem: VFC<{
  title: string
  id: string
  placeholder: string
  checked: boolean
  minRows: number
}> = ({ id, title, placeholder, checked, minRows }) => {
  const { register } = useFormContext()

  return (
    <>
      <ItemTitle title={title} checked={checked} optional />
      <TextArea
        minRows={minRows}
        id={id}
        placeholder={placeholder}
        {...register(id, {
          required: false,
          maxLength: {
            value: NOTE_MAX_LENGTH,
            message: `文字数は${NOTE_MAX_LENGTH}文字以内にお願いいたします`,
          },
        })}
      />
    </>
  )
}
